import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useEntitlements } from "../hooks/useEntitlements";



import "../styles/generalStyle.css";
import "./CreateSongIdeas.css";

import { useLanguage } from "../i18n/LanguageContext";

export default function CreateSongIdeas() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const {
    loading: entLoading,
    canSong,
    consumeSongCredit,
    entitlements
  } = useEntitlements(firebaseUser?.uid);


  const [estilo, setEstilo] = useState("");
  const [instrumento, setInstrumento] = useState("");
  const [voz, setVoz] = useState("hombre"); // ✅ default hombre
  const [ideas, setIdeas] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [createdId, setCreatedId] = useState(null);



  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  const ideasLen = ideas.trim().length;

  const prompt = useMemo(() => {
    const parts = [
      `${t("createSong.summaryStyle")}: ${estilo?.trim() || "-"}`,
      instrumento?.trim()
        ? `${t("createSong.summaryInstrument")}: ${instrumento.trim()}`
        : null,
      `${t("createSong.summaryVoice")}: ${
        voz === "mujer" ? t("createSong.voiceFemale") : t("createSong.voiceMale")
      }`,
      `${t("createSong.summaryIdeas")}: ${ideas?.trim() || "-"}`,
    ].filter(Boolean);

    return parts.join(" | ");
  }, [t, estilo, instrumento, voz, ideas]);

  const buildTitulo = () => {
    const base = estilo.trim()
      ? `${t("createSong.summaryStyle")} ${estilo.trim()}`
      : "Canción personalizada";
    return base.length > 60 ? base.slice(0, 60) : base;
  };

  const validate = () => {
    if (!firebaseUser) return t("createSong.errors.notLogged");
    if (!estilo.trim()) return t("createSong.styleRequiredError");
    if (!voz) return t("createSong.voiceRequiredError"); // si no la tienes, te digo abajo
    if (ideas.trim().length < 100) return t("createSong.ideasRequiredError");
    return "";
  };

  const handleCreate = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    if (!canSong) {
      setError(t("limites.limiteAlcanzadoCancion"));
      return;
    }


    try {
      setSaving(true);
      setError("");
      setCreatedId(null);

      // ✅ DESCONTAR (demo o paid) según corresponda
      await consumeSongCredit();


      const cancionesCol = collection(db, "usuarios", firebaseUser.uid, "canciones");
      const docRef = doc(cancionesCol);

      const data = {
        id: docRef.id,
        userId: firebaseUser.uid,

        estilo: estilo.trim() + voz,
        instrumento: instrumento.trim() ? instrumento.trim() : null,
        voz: voz, // "hombre" | "mujer"
        prompt: prompt,

        titulo: buildTitulo(),
        letra: "",
        pago: false,

        status: "pending",
        coverUrl: "",
        coverUrl02: "",
        desdeWeb: true,


        fechaCreacion: serverTimestamp(),
      };

      await setDoc(docRef, data, { merge: true });
            // ✅ Crear/actualizar doc mini para compartir (cancionesWeb/{songId})
      const miniRef = doc(db, "cancionesWeb", docRef.id);

      const miniData = {
        songId: docRef.id,
        usuarioId: firebaseUser.uid,

        coverUrl: "",
        coverUrl02: "",
        videoUrl: "",
        videoUrl02: "",

        status: "pending",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(miniRef, miniData, { merge: true });


      setCreatedId(docRef.id);
    } catch (e) {
      console.error("Error creando canción:", e);
      // ✅ si falló por créditos
      if (String(e?.message || "").includes("NO_SONG_CREDITS")) {
        setError(t("limites.limiteAlcanzadoCancion"));
      } else {
        setError(t("createSong.errors.generic"));
      }
      
    } finally {
      setSaving(false);
    }
  };

  if (!firebaseUser) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("createSong.notLoggedTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("createSong.notLoggedText")}
            </p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => navigate("/login")}
            >
              {t("createSong.goToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (createdId) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("createSong.successTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 10 }}>
              {t("createSong.successText")}
            </p>

            <div className="createdBox">
              <div className="createdRow">
                <span className="createdLabel">{t("createSong.statusLabel")}</span>
                <span className="createdValue">pending</span>
              </div>
              <div className="createdRow">
                <span className="createdLabel">{t("createSong.idLabel")}</span>
                <span className="createdValue">{createdId}</span>
              </div>
            </div>

            <button
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => navigate("/app/songs")}
            >
              {t("createSong.goToSongs")}
            </button>

            <button
              className="btn"
              style={{ marginTop: 10 }}
              onClick={() => {
                setCreatedId(null);
                setError("");
                setEstilo("");
                setInstrumento("");
                setVoz("hombre");
                setIdeas("");
              }}
            >
              {t("createSong.createAnother")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="grid-2">
          <section className="card">
            <h1 className="h1">{t("createSong.title")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("createSong.subtitle")}
            </p>

            <div className="form" style={{ marginTop: 10 }}>
              <label className="label">
                {t("createSong.styleLabel")} *
                <input
                  className="input"
                  value={estilo}
                  onChange={(e) => setEstilo(e.target.value)}
                  placeholder={t("createSong.stylePlaceholder")}
                  maxLength={80}
                />
              </label>

              <label className="label">
                {t("createSong.instrumentLabel")}
                <input
                  className="input"
                  value={instrumento}
                  onChange={(e) => setInstrumento(e.target.value)}
                  placeholder={t("createSong.instrumentPlaceholder")}
                  maxLength={80}
                />
                <span className="help-text">{t("createSong.instrumentHelp")}</span>
              </label>

              <div className="label">
                {t("createSong.voiceLabel")} *
                <div className="voiceRow">
                  <button
                    type="button"
                    className={`voiceChip ${voz === "hombre" ? "active" : ""}`}
                    onClick={() => setVoz("hombre")}
                  >
                    {t("createSong.voiceMale")}
                  </button>
                  <button
                    type="button"
                    className={`voiceChip ${voz === "mujer" ? "active" : ""}`}
                    onClick={() => setVoz("mujer")}
                  >
                    {t("createSong.voiceFemale")}
                  </button>
                </div>
                <span className="help-text">{t("createSong.voiceHelp")}</span>
              </div>

              <label className="label">
                {t("createSong.ideasLabel")} * ({t("createSong.ideasMin")})
                <textarea
                  className="input textarea"
                  value={ideas}
                  onChange={(e) => setIdeas(e.target.value)}
                  placeholder={t("createSong.ideasPlaceholder")}
                  rows={6}
                />
                <span className={`help-text ${ideasLen < 100 ? "warn" : ""}`}>
                  {t("createSong.ideasCounter", { count: ideasLen })}
                </span>
              </label>

              {error ? <div className="alert-error">{error}</div> : null}

              <div className="hintBox">
                <p className="hintText">{t("createSong.hintText")}</p>
                <Link className="hintLink" to="/app/letras">
                  {t("createSong.goToLyrics")}
                </Link>
              </div>

              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || entLoading || !canSong}
                onClick={handleCreate}
              >
                {saving ? t("createSong.generatingButton") : t("createSong.generateButton")}
              </button>

              {!entLoading && !canSong ? (
                <div className="alert-error" style={{ marginTop: 10 }}>
                  {t("limites.limiteAlcanzadoCancion")}
                 
                </div>
              ) : null}

              <p className="note">{t("createSong.note")}</p>
            </div>
          </section>

          <section className="card">
            <h2 className="h2">{t("createSong.summaryTitle")}</h2>
            <p className="p-muted">{t("createSong.summarySubtitle")}</p>

            <div className="summaryBox">
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSong.summaryStyle")}</span>
                <span className="summaryValue">{estilo.trim() || "--"}</span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSong.summaryInstrument")}</span>
                <span className="summaryValue">{instrumento.trim() || "--"}</span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSong.summaryVoice")}</span>
                <span className="summaryValue">
                  {voz === "mujer" ? t("createSong.voiceFemale") : t("createSong.voiceMale")}
                </span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSong.summaryIdeas")}</span>
                <span className="summaryValue">
                  {ideas.trim() ? `✅ ${t("createSong.summaryIdeasDone")}` : "--"}
                </span>
              </div>
            </div>

            <div className="summaryPrompt">
              <span className="summaryLabel">{t("createSong.summaryPrompt")}</span>
              <div className="promptBox">{prompt}</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
